import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import { ArrowDown, Play } from "lucide-react";

const HERO_FALLBACK_IMG =
  "https://images.unsplash.com/photo-1497008323932-4f726e0f13f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

const wordVariants = {
  hidden: { y: "110%" },
  visible: (i = 0) => ({
    y: 0,
    transition: {
      delay: 0.2 + i * 0.08,
      duration: 1.1,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const Word = ({ children, i }) => (
  <span className="reveal-mask align-bottom">
    <motion.span
      custom={i}
      variants={wordVariants}
      initial="hidden"
      animate="visible"
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useI18n();

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const title1Words = t.hero.title_1.split(" ");
  const title2Words = t.hero.title_2.split(" ");

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-end overflow-hidden bg-black"
    >
      {/* Cinematic background */}
      <div
        className="absolute inset-0 animate-ken-burns"
        style={{
          backgroundImage: `url(${HERO_FALLBACK_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 1,
        }}
        data-testid="hero-background-image"
      />

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
      <div className="absolute inset-0 bg-black/30" />
      <div className="grain" />

      {/* Side rail labels */}
      <div className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 text-[10px] tracking-[0.4em] text-white/40 rotate-180" style={{ writingMode: "vertical-rl" }}>
        <span>SUE · PRODUCTION · STUDIO</span>
      </div>

      <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 text-[10px] tracking-[0.4em] text-lux/70" style={{ writingMode: "vertical-rl" }}>
        <span>MIAMI · TULSA · DALLAS</span>
      </div>

      {/* Content */}
      <div className="relative container mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-32 z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-lux" />
          <span data-testid="hero-eyebrow" className="text-[11px] tracking-[0.3em] uppercase text-lux">
            {t.hero.eyebrow}
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-display font-light text-5xl sm:text-7xl lg:text-[8.5rem] leading-[0.95] tracking-tighter max-w-5xl"
        >
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {title1Words.map((w, i) => (
              <Word key={`a-${i}`} i={i}>
                {w}
              </Word>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 italic font-extralight">
            {title2Words.map((w, i) => (
              <Word key={`b-${i}`} i={title1Words.length + i}>
                <span className="text-gradient-lux">{w}</span>
              </Word>
            ))}
          </div>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 max-w-xl text-base md:text-lg text-white/70 font-body leading-relaxed text-balance"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => go("quote")}
            className="group inline-flex items-center justify-center gap-3 bg-white text-black rounded-full px-7 py-4 text-sm uppercase tracking-[0.22em] font-medium hover:bg-zinc-200 transition-all duration-300 hover:scale-[1.02]"
          >
            {t.hero.cta_primary}
            <span className="w-1.5 h-1.5 rounded-full bg-black group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => go("portfolio")}
            className="group inline-flex items-center justify-center gap-3 bg-white/5 backdrop-blur-md border border-white/20 text-white rounded-full px-7 py-4 text-sm uppercase tracking-[0.22em] font-medium hover:bg-white/10 hover:border-white/40 transition-all duration-300"
          >
            <Play size={14} className="opacity-70" />
            {t.hero.cta_secondary}
          </button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-6 md:right-12 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-white/50"
        >
          {t.hero.scroll}
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="inline-flex"
          >
            <ArrowDown size={14} />
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
}
